import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'

import { LOGIN_PATH } from 'account/login/Login'
import { Layout as AntLayout, Col, Row, Skeleton } from 'antd'

import { StepStatus, WizardStep } from 'core/domain/wizard/wizard'

import Tooltip from 'common/components/dataDisplay/Tooltip'
import Button from 'common/components/general/Button'
import { pushState } from 'common/components/utils/WindowUtils'

import { useBreakPoints } from 'common/hooks'

import Layout from '../../common/layout/Layout'
import { disclaimerModal } from './DisclaimerModal'
import { SidebarNavigation } from './SidebarNavigation'
import { NavItemProps } from './models'

import './DesignStyles.less'

const { Content, Sider } = AntLayout

declare type BackButtonType = 'back'
declare type NextButtonType = 'next' | 'finish'

interface EmployeeSignupProps {
  signupFlowPath: string
  isUserAllowedToEnterSignupFlow: boolean
  employeeSignupWizard: () => Promise<any>
  employeeSignupStateApi: () => Promise<any>
  navItemsMapping: NavItemProps[]
}

const EmployeeSignup: React.FC<EmployeeSignupProps> = ({
  signupFlowPath,
  isUserAllowedToEnterSignupFlow,
  employeeSignupWizard,
  employeeSignupStateApi,
  navItemsMapping,
}) => {
  const history = useHistory()
  const match = useRouteMatch()
  const { step } = useParams<any>()
  const employeeSignupFullPath = match.path.substring(0, match.path.indexOf(signupFlowPath) + signupFlowPath.length)
  const [navigationItems, setNavigationItems] = useState<WizardStep[]>([])
  const [currentNavigationItem, setCurrentNavigationItem] = useState<NavItemProps>()
  const [employeeSignupState, setEmployeeSignupState] = useState<any>()
  const [isFormValid, setIsFormValid] = useState<boolean>(false)
  const [isSubmiting, setIsSubmiting] = useState<boolean>(false)
  const [hasDirtyData, setHasDirtyData] = useState<boolean>(false)
  const { t: translate } = useTranslation()
  const { isLg } = useBreakPoints()

  let goBackMethod: () => void
  let goNextMethod: () => void
  let getIndexOfActiveNavigationItemMethod: (items: WizardStep[]) => number
  let setEditingNavigationItemMethod: (editingStep: string | undefined) => void
  let submitFormHandler: () => Promise<any>

  useEffect(() => {
    if (!isUserAllowedToEnterSignupFlow) redirectToLogin()
    else {
      employeeSignupWizard().then((response: WizardStep[]) => {
        setNavigationItems(response)
      })
    }
  }, [])

  useEffect(() => {
    try {
      if (navigationItems.length > 0) {
        if (step) {
          const getCurrentNavigationItem = navigationItems.find((item) => item.id === step)
          if (!getCurrentNavigationItem) throw new Error('Navigation item not found')
          if (getCurrentNavigationItem.status !== StepStatus.Disabled) {
            const navigationItemProps = navItemsMapping.find((navItem) => navItem.key === step)
            setEditingNavigationItemMethod(step)
            setCurrentNavigationItem(navigationItemProps)
          } else {
            loadActiveNavigationItem(navigationItems)
          }
        } else {
          loadActiveNavigationItem(navigationItems)
        }
      }
    } catch (e) {
      console.log(e)
    }
  }, [navigationItems])

  useEffect(() => {
    if (currentNavigationItem) {
      setHasDirtyData(false)
      setIsSubmiting(false)
      pushState(`${employeeSignupFullPath}/${currentNavigationItem?.key}`)
      employeeSignupStateApi().then((response: any) => {
        setEmployeeSignupState(response)
      })
      setEmployeeSignupState({
        personalEmail: null,
        phoneNumber: null,
        familyStatusId: null,
      })
    }
  }, [currentNavigationItem])

  const loadActiveNavigationItem = (items: WizardStep[]) => {
    const index = getIndexOfActiveNavigationItemMethod(items)
    const navigationItem = items[index]
    const navigationItemProps = navItemsMapping.find((navItem) => navItem.key === navigationItem.id)
    setCurrentNavigationItem(navigationItemProps)
  }

  const getBackButton = (navigationItems: WizardStep[]): ReactNode => {
    if (navigationItems.length > 0 && currentNavigationItem && currentNavigationItem.key !== navigationItems[0].id) {
      return getButtonBasedOnType('back')
    }
    return null
  }

  const getNextButton = (navigationItems: WizardStep[]): ReactNode => {
    if (navigationItems.length > 0 && currentNavigationItem) {
      let buttonType: NextButtonType = 'next'
      if (currentNavigationItem.key === navigationItems[navigationItems.length - 1].id) {
        buttonType = 'finish'
      }

      const button = getButtonBasedOnType(buttonType)

      if (isNextButtonDisabled()) {
        return (
          <Tooltip title={currentNavigationItem.nextButtonTooltip} position="left">
            {button}
          </Tooltip>
        )
      } else {
        return button
      }
    }
    return null
  }

  const getButtonBasedOnType = (type: BackButtonType | NextButtonType) => {
    switch (type) {
      case 'back':
        return (
          <Button size="default" icon="fa fa-arrow-left" htmlType="button" type="default" onClick={backHandler}>
            Back
          </Button>
        )
      case 'next':
        return (
          <Button
            icon="fa fa-arrow-right"
            iconPosition="right"
            size="default"
            htmlType="submit"
            type="primary"
            disabled={isNextButtonDisabled()}
            onClick={submitHandler}
            className="right"
            loading={isSubmiting}
          >
            {isSubmiting ? 'Saving' : 'Next'}
          </Button>
        )
      case 'finish': {
        return (
          <Button
            icon="fa fa-arrow-right"
            iconPosition="right"
            size="default"
            htmlType="submit"
            type="primary"
            disabled={isNextButtonDisabled()}
            onClick={submitHandler}
            className="right"
            loading={isSubmiting}
          >
            {isSubmiting ? 'Saving' : 'Finish'}
          </Button>
        )
      }
    }
  }

  const isNextButtonDisabled = (): boolean => {
    if (!isFormValid) {
      return true
    }
    return false
  }

  const backHandler = () => {
    if (hasDirtyData) {
      disclaimerModal(() => {
        goBackMethod()
      }, translate)
    } else {
      goBackMethod()
    }
  }

  const submitHandler = () => {
    setIsSubmiting(true)
    submitFormHandler()
  }

  const goNext = () => {
    goNextMethod()
  }

  const finish = () => {
    setIsSubmiting(false)
    window.location.href = '/'
  }

  const redirectToLogin = () => {
    history.push(LOGIN_PATH)
  }

  const renderSidebar = (isMobile = false) => (
    <SidebarNavigation
      navItemMapping={navItemsMapping}
      navigationItems={navigationItems}
      hasDirtyData={hasDirtyData}
      setCurrentNavigationItem={setCurrentNavigationItem}
      bindGoBack={(goBack: () => void) => {
        goBackMethod = goBack
      }}
      bindGoNext={(goNext: () => void) => {
        goNextMethod = goNext
      }}
      bindGetIndexOfActiveNavigationItem={(getIndexOfActiveNavigationItem: (items: WizardStep[]) => number) => {
        getIndexOfActiveNavigationItemMethod = getIndexOfActiveNavigationItem
      }}
      bindSetEditingNavigationItem={(setEditingItem: (editingStep: string | undefined) => void) => {
        setEditingNavigationItemMethod = setEditingItem
      }}
      showDisclaimerModal={disclaimerModal}
      isMobile={isMobile}
    />
  )

  return (
    <Layout>
      <AntLayout className="ee_signup_flow_content">
        {isLg ? (
          <Sider className="ee_sidebar_navigation" collapsedWidth={0} width={240}>
            {renderSidebar()}
          </Sider>
        ) : (
          <Sider className="ee_sidebar_navigation" collapsedWidth={0} width={35}>
            {renderSidebar(true)}
          </Sider>
        )}

        <Content className="ee_signup_flow_body">
          <Skeleton loading={!currentNavigationItem || !employeeSignupState} active>
            {currentNavigationItem ? (
              <currentNavigationItem.component
                name={currentNavigationItem.key}
                setIsFormValid={setIsFormValid}
                setIsSubmiting={setIsSubmiting}
                bindSubmitFormHandler={(submitForm: any) => {
                  submitFormHandler = submitForm
                }}
                state={currentNavigationItem.mapState(employeeSignupState)}
                goNext={goNext}
                finish={finish}
                hasDirtyData={setHasDirtyData}
              />
            ) : null}
          </Skeleton>
          <div className="wizard_footer">
            {employeeSignupState ? (
              <Row>
                <Col span={2}></Col>
                <Col span={20}>
                  {getBackButton(navigationItems)} {getNextButton(navigationItems)}
                </Col>
              </Row>
            ) : null}
          </div>
        </Content>
      </AntLayout>
    </Layout>
  )
}

export default EmployeeSignup
